import type { FetchError, StoreStockReorderResponse } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import { invalidateSellerCarts } from "../cart/cache";
import { stockTrackingQueryKeys } from "./queryKeys";

/**
 * Client data layer for the variant-keyed **stock reorder** path (#72 / #73 over
 * api#152). A tracked item is a bare, supplier-agnostic `variant_id`; the seller
 * cart is strict on `offer_id`, so the server resolves each variant to an offer
 * (last-ordered seller, else cheapest active in-stock) and returns the
 * changed-line diff. Two phases, mirroring `/store/reorder` (ADR-0002):
 *
 * - **preview** (`dry_run: true`) - resolve without mutating a cart. Exposed two
 *   ways: a `useQuery` for the inline "from <supplier> - $X" a due row shows
 *   (cached, auto-fetched), and a mutation for the fresh on-click resolve that
 *   opens the review dialog.
 * - **confirm** (`dry_run: false`) - re-resolve and add the available + repriced
 *   lines to the seller cart(s); invalidates the seller carts so the cart UI and
 *   header badge refresh. Reorder never changes `on_hand`, so the tracked list
 *   (and the "due" state) is untouched here - the post-reorder count prompt is
 *   what edits counts, via the stock-tracking upsert.
 */

// ============================================================
// Preview (inline supplier + price)
// ============================================================

/**
 * Dry-run resolve of a set of tracked variants against live offers, for the
 * inline supplier + price on the "Due to Reorder" rows. Keyed by the sorted
 * variant set; disabled for an empty set. Kept short-lived since offers/prices
 * move.
 */
export function stockReorderPreviewQuery(variantIds: string[]) {
  return queryOptions({
    queryKey: stockTrackingQueryKeys.reorderPreview(variantIds),
    queryFn: () => sdk.store.stockTracking.previewReorder(variantIds),
    enabled: variantIds.length > 0,
    staleTime: 60 * 1000
  });
}

export function useStockReorderPreviewQuery(
  variantIds: string[],
  options?: { enabled?: boolean }
) {
  const base = stockReorderPreviewQuery(variantIds);
  return useQuery({
    ...base,
    ...options,
    // Never override the empty-set guard to `true`.
    enabled: base.enabled && (options?.enabled ?? true)
  });
}

// ============================================================
// Mutations
// ============================================================

/**
 * Fresh dry-run resolve on click - resolves the given tracked variants and
 * returns the changed-line diff WITHOUT mutating any cart. Drives the review
 * dialog. Does not invalidate anything.
 */
export function useStockReorderPreview() {
  return useMutation<StoreStockReorderResponse, FetchError, string[]>({
    mutationFn: (variantIds) =>
      sdk.store.stockTracking.previewReorder(variantIds)
  });
}

/**
 * Confirm a stock reorder: re-resolve the given tracked variants and add their
 * available + repriced lines (qty 1 each) to the appropriate seller cart(s).
 * Invalidates the seller carts so the cart page and header count refresh.
 */
export function useStockReorderConfirm() {
  const queryClient = useQueryClient();

  return useMutation<StoreStockReorderResponse, FetchError, string[]>({
    mutationFn: (variantIds) =>
      sdk.store.stockTracking.confirmReorder(variantIds),
    onSettled: () => {
      invalidateSellerCarts(queryClient);
    }
  });
}
